import type { HtmlAttributes } from '@studio/design-system/props';
import type { Settings } from '@studio/models/control';
import { Protocol } from '@studio/models/control';
import type { Configuration } from '@studio/models/control/http';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn, useMount } from '@studio/react-utils/hooks';
import type { T_Nullable } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { shallow } from 'zustand/shallow';
import { useProducer, useStore } from '../store/hooks.js';
import { resolveProtocolSettings } from './normalize.js';
import { Http as _Http } from '#~/control/components/editor/index.js';

/** @internal */
const Inner = (props: HtmlAttributes): ReactElement => {
  const producer = useProducer();

  const settings = useStore(
    ({ candidate }) =>
      // We know this is a normalized HTTP configuration.
      candidate.control as Settings<Configuration>,
    shallow
  );

  const handleChange = useLatestFn((value: Settings) => {
    producer((draft) => {
      draft.candidate.control = value;
    });
  });

  return <_Http settings={toAccessor(settings, handleChange)} {...props} />;
};

/** HTTP settings. */
export const Http = (props: HtmlAttributes): T_Nullable<ReactElement> => {
  const [isPrimed, setPrimed] = useState(false);
  const producer = useProducer();

  // On mount, normalize the HTTP control settings.
  useMount(() => {
    queueMicrotask(() => {
      producer((draft) => {
        const {
          candidate,
          manufacturer: { config }
        } = draft;

        const { control, video } = candidate;

        // Moved verbatim to normalize.ts so the first-run funnel resolves the
        // same endpoint without rendering this panel. See that file's header.
        draft.candidate.control = resolveProtocolSettings(
          Protocol.HTTP,
          control,
          config,
          video
        ) as Settings<Configuration>;
      });

      setPrimed(true);
    });
  });

  return isPrimed ? <Inner {...props} /> : null;
};
